/**
 * Router construction (spec §14.1).
 *
 * `createMadalineRouter` takes its dependencies rather than importing them, so a
 * test can drive the whole application — guards, redirects, screens — against a
 * stub API with no network and no module mocking.
 */
import { createRouter, type RouterHistory } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { RouteError, RouteNotFound, RoutePending } from "../routes/route-states.js";
import { routeTree, type RouterContext } from "../routes/tree.js";
import { createQueryClient } from "./query-client.js";
import { createMadalineApi, type MadalineApi } from "./api.js";

export interface CreateMadalineRouterOptions {
  api?: MadalineApi;
  queryClient?: QueryClient;
  history?: RouterHistory;
}

export function createMadalineRouter(options: CreateMadalineRouterOptions = {}) {
  const queryClient = options.queryClient ?? createQueryClient();
  const api = options.api ?? createMadalineApi();
  const context: RouterContext = { api, queryClient };

  return createRouter({
    routeTree,
    context,
    ...(options.history ? { history: options.history } : {}),
    // §14.7: error boundaries at route level. The three states a route can be
    // in instead of its screen are designed, and drawn inside the signed-in
    // frame when the address is under `/app` (`routes/route-states.tsx`).
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: RouteNotFound,
    defaultPendingComponent: RoutePending,
    // A stale document is never what an editor wants to see.
    defaultPreload: false,
    scrollRestoration: true,
  });
}

export type MadalineRouter = ReturnType<typeof createMadalineRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: MadalineRouter;
  }
}
